/**
 * Form state management for the New Message modal.
 * Uses Yup schema with channel-aware .when() rules so formState.isValid is the
 * single source of truth for both the Send button and submission validation.
 */

import { useCallback } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { BrainVersionSelection } from '@/components/atoms/BrainVersionSelector';
import { IntegrationType } from '@/shared/types/integration';
import {
  areAllTemplateVariablesFilled,
  EMAIL_BODY_CHARACTER_LIMIT,
  EMAIL_SUBJECT_CHARACTER_LIMIT,
  phoneValidation,
  SMS_CHARACTER_LIMIT,
} from '@/util/validation/patterns';

const isPhoneChannel = (type: string | null | undefined): boolean =>
  !!type && type !== IntegrationType.EMAIL && type !== IntegrationType.WHATSAPP;

const newMessageFormSchema = yup.object({
  integrationType: yup.string().nullable().default(null),

  toPhone: phoneValidation.default('').when('integrationType', ([type], schema) => {
    if (isPhoneChannel(type) || type === IntegrationType.WHATSAPP) {
      return schema.required('conversation.new_message.validation.phone_required');
    }
    return schema.optional();
  }),

  toEmail: yup
    .string()
    .email('conversation.new_message.validation.invalid_email')
    .default('')
    .when('integrationType', {
      is: IntegrationType.EMAIL,
      then: (schema) => schema.required('conversation.new_message.validation.email_required'),
      otherwise: (schema) => schema.optional(),
    }),

  integrationId: yup.string().required('conversation.new_message.validation.channel_required'),

  templateId: yup
    .string()
    .nullable()
    .when('integrationType', {
      is: IntegrationType.WHATSAPP,
      then: (schema) =>
        schema
          .required('conversation.new_message.validation.template_required')
          .test(
            'variables-filled',
            'conversation.new_message.validation.variables_required',
            function () {
              const { headerParameters, bodyParameters, footerParameters } = this.parent;
              return (
                areAllTemplateVariablesFilled(headerParameters) &&
                areAllTemplateVariablesFilled(bodyParameters) &&
                areAllTemplateVariablesFilled(footerParameters)
              );
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),

  headerParameters: yup.array(yup.object({ name: yup.string().default('') })).default([]),
  bodyParameters: yup.array(yup.object({ name: yup.string().default('') })).default([]),
  footerParameters: yup.array(yup.object({ name: yup.string().default('') })).default([]),
  headerMediaParam: yup
    .object({ type: yup.string().default(''), value: yup.string().default('') })
    .nullable(),

  content: yup
    .string()
    .default('')
    .when('integrationType', ([type], schema) => {
      if (type === IntegrationType.INFOBIP_SMS) {
        return schema
          .required('conversation.new_message.validation.content_required')
          .max(SMS_CHARACTER_LIMIT);
      }
      if (type === IntegrationType.INFOBIP_RCS || type === IntegrationType.EMAIL) {
        return schema
          .required('conversation.new_message.validation.content_required')
          .max(EMAIL_BODY_CHARACTER_LIMIT);
      }
      return schema.optional().max(EMAIL_BODY_CHARACTER_LIMIT);
    }),

  subject: yup
    .string()
    .max(EMAIL_SUBJECT_CHARACTER_LIMIT)
    .default('')
    .when('integrationType', {
      is: IntegrationType.EMAIL,
      then: (schema) => schema.required('conversation.new_message.validation.subject_required'),
      otherwise: (schema) => schema.optional(),
    }),

  recipientName: yup.string().optional().default(''),

  aiAgent: yup
    .object({
      brain_id: yup.string().nullable(),
      brain_parent_id: yup.string().required(),
      brain_version: yup.number().nullable(),
      brain_name: yup.string().nullable(),
    })
    .nullable()
    .default(null),
});

export type NewMessageFormValues = yup.InferType<typeof newMessageFormSchema>;

interface UseNewMessageFormOptions {
  initialToPhone?: string;
  initialToEmail?: string;
  initialIntegrationId?: string;
}

/** Manages form state, validation, and field arrays for WhatsApp template variables. */
export const useNewMessageForm = (options?: UseNewMessageFormOptions) => {
  const form = useForm<NewMessageFormValues>({
    resolver: yupResolver<NewMessageFormValues, unknown, NewMessageFormValues>(
      newMessageFormSchema
    ),
    defaultValues: {
      integrationType: null,
      toPhone: options?.initialToPhone ?? '',
      toEmail: options?.initialToEmail ?? '',
      integrationId: options?.initialIntegrationId ?? '',
      templateId: null,
      headerParameters: [],
      bodyParameters: [],
      footerParameters: [],
      headerMediaParam: null,
      recipientName: '',
      content: '',
      subject: '',
      aiAgent: null,
    },
    mode: 'onChange',
  });

  const {
    control,
    setValue,
    reset,
    formState: { isDirty, isValid },
  } = form;

  const { fields: headerFields, replace: headerReplace } = useFieldArray({
    control,
    name: 'headerParameters',
  });
  const { fields: bodyFields, replace: bodyReplace } = useFieldArray({
    control,
    name: 'bodyParameters',
  });
  const { fields: footerFields, replace: footerReplace } = useFieldArray({
    control,
    name: 'footerParameters',
  });

  const onContentChange = useCallback(
    (newContent: string) => {
      setValue('content', newContent, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const onSubjectChange = useCallback(
    (newSubject: string) => {
      setValue('subject', newSubject, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const onAiAgentChange = useCallback(
    (selection: BrainVersionSelection | null) => {
      setValue('aiAgent', selection, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  return {
    form,
    isValid,
    isDirty,
    resetForm: reset,
    headerFields,
    bodyFields,
    footerFields,
    headerReplace,
    bodyReplace,
    footerReplace,
    onContentChange,
    onSubjectChange,
    onAiAgentChange,
  };
};
